import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { AuthService } from './auth.service';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export class BaseService {
  constructor(
    protected authService: AuthService,
    protected http: HttpClient
  ) {}

  protected async getHeaders(): Promise<HttpHeaders> { 
    if(this.authService.isTokenExpired())
      await this.authService.refreshToken();

    const authorizationToken = localStorage.getItem('legalresources-access-token');
    return new HttpHeaders({
      'Authorization': `Bearer ${authorizationToken}`
    });
  }

  // Wrapper methods that handle headers automatically
  protected get<T>(url: string, options?: { params?: HttpParams, customHeaders?: { [key: string]: string } }): Observable<T> {
    return from(this.getHeaders()).pipe(
      switchMap(authHeaders => {
        let headers = authHeaders;
        if (options?.customHeaders) {
          Object.keys(options.customHeaders).forEach(key => {
            headers = headers.set(key, options.customHeaders![key]);
          });
        }
        const httpOptions = {
          headers,
          ...(options?.params && { params: options.params })
        };
        return this.http.get<T>(url, httpOptions);
      })
    );
  }

  protected post<T>(url: string, body: any, options?: { params?: HttpParams, customHeaders?: { [key: string]: string } }): Observable<T> {
    return from(this.getHeaders()).pipe(
      switchMap(authHeaders => {
        let headers = authHeaders;
        
        // Add custom headers if provided
        if (options?.customHeaders) {
          Object.keys(options.customHeaders).forEach(key => {
            if (!(body instanceof FormData && key.toLowerCase() === 'content-type')) {
              headers = headers.set(key, options.customHeaders![key]);
            }
          });
        }
        
        const httpOptions = {
          headers,
          ...(options?.params && { params: options.params })
        };
        return this.http.post<T>(url, body, httpOptions);
      })
    );
  }

  protected put<T>(url: string, body: any, options?: { params?: HttpParams, customHeaders?: { [key: string]: string } }): Observable<T> {
    return from(this.getHeaders()).pipe(
      switchMap(authHeaders => {
        let headers = authHeaders;
        
        // Add custom headers if provided
        if (options?.customHeaders) {
          Object.keys(options.customHeaders).forEach(key => {
            headers = headers.set(key, options.customHeaders![key]);
          });
        }
        
        const httpOptions = {
          headers,
          ...(options?.params && { params: options.params })
        };
        return this.http.put<T>(url, body, httpOptions);
      })
    );
  }

  protected delete<T>(url: string, options?: { params?: HttpParams }): Observable<T> {
    return from(this.getHeaders()).pipe(
      switchMap(headers => {
        const httpOptions = {
          headers,
          ...(options?.params && { params: options.params })
        };
        return this.http.delete<T>(url, httpOptions);
      })
    );
  }

  protected patch<T>(url: string, body: any, options?: { params?: HttpParams }): Observable<T> {
    return from(this.getHeaders()).pipe(
      switchMap(headers => {
        const httpOptions = {
          headers,
          ...(options?.params && { params: options.params })
        };
        return this.http.patch<T>(url, body, httpOptions);
      })
    );
  }
}
